import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Bugsnag from '@bugsnag/js';
import styled from 'styled-components';
import { VistaWordmark } from '@99designs/branding';
import { Box, Button, Inline, Link, Tag, Tooltip } from '@99designs/emu';
import { mediaQuery } from '@99designs/emu/css';
import { spaces, typography } from '@99designs/emu/settings';
import { JobCategory, JobSource, Maybe, Owner } from '@99designs/graph-utils/types';
import { Currency, RelativeTimeDisplay } from '@99designs/i18n';
import { IconDate1, IconServer1, IconService1 } from '@99designs/icons';
import { linkTo } from '@99designs/routing';
import { PartnerContext, partnerFromCategory } from '../Briefs';
import { DismissJobButton } from '../DismissJobButton';
import { Desktop, Mobile } from '../Hidden';
import AuthorDisplay from './AuthorDisplay';
import { ItemIcon } from './ItemIcon';
import { JobCardContainer } from './JobCardContainer';
import { JobCardTitle } from './JobCardTitle';
import TextWithClampedOverflow from './TextWithClampedOverflow';

const ContentWrapper = styled(Box).attrs(() => ({
    display: 'flex',
    width: ['100%', 'none'],
}))`
    gap: ${spaces['1bu']};
`;

const ActionsInfoWrapper = styled(Box).attrs(() => ({
    display: 'flex',
    flexDirection: ['row-reverse', 'column'],
    width: ['100%', 'none'],
}))`
    gap: ${spaces['1.5bu']};

    ${mediaQuery.minimum('normal')} {
        min-width: 232px;
    }

    ${mediaQuery.between('small', 'medium')} {
        align-items: center;
        justify-content: space-between;
    }
`;

const StyledWordmarkWrapper = styled.div`
    width: 100px;
    display: flex;
    align-items: center;
    justify-content: center;

    & > div {
        width: 100%;
        height: auto;
    }

    & svg {
        width: 100%;
        height: auto;
        display: block;
    }
`;

const ActionsWrapper = styled.div`
    z-index: 3;
`;

const DottedUnderline = styled.span`
    text-decoration: underline dotted currentColor;
    text-underline-offset: 0.25em;

    ${mediaQuery.between('small', 'medium')} {
        text-decoration: none;
    }
`;

const InfoWrapper = styled(Box).attrs(() => ({
    display: 'flex',
    flexDirection: ['row', 'column'],
}))`
    gap: ${spaces['0.75bu']};

    ${mediaQuery.between('small', 'medium')} {
        gap: 0 ${spaces['1.5bu']};
        flex-wrap: wrap;
    }
`;

const InfoItem = styled(Box).attrs(() => ({
    display: 'flex',
}))`
    font-size: ${typography.fontSizes.paragraphs['small']}px;

    ${mediaQuery.minimum('medium')} {
        gap: ${spaces['1bu']};
    }

    ${mediaQuery.between('small', 'medium')} {
        gap: ${spaces['0.5bu']};

        * {
            text-decoration: none;
        }
    }
`;

const RouterLinkOverlay = styled(RouterLink)`
    position: absolute;
    z-index: 2;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
`;

const DisabledCardContainer = styled(JobCardContainer)`
    pointer-events: none;
`;

const FallbackCopy = 'Not available';

type ListingProduct =
    | ({ __typename: 'ProductFixedPriceProject' } & Maybe<{
          key: string;
          title: string;
          description: string;
          pricing: {
              earningsInUsd: number;
          };
      }>)
    | ({ __typename: 'DesignService' } & Maybe<{
          __typename: 'DesignService';
          mpvId: string;
          title: string;
          price: number;
          currency: string;
      }>);

interface ProductDetails {
    title: string;
    description: string;
    earnings: number;
}

export interface JobCardProps {
    jobForDesigner: {
        job: {
            id: string;
            createdAt: string;
            user: Owner;
            source: JobSource;
            category: Maybe<JobCategory>;
            flags: { __typename: 'JobFlags'; test: boolean };
            product: ListingProduct;
            brief: Maybe<{
                title: string;
                currency: string;
                description: string;
                industries: Array<{ key: string; title: string }>;
            }>;
        };
    };
    onCtaActionCompleted?: () => void;
    dismissible: boolean;
}

function getProductDetails(product: ListingProduct): ProductDetails {
    switch (product.__typename) {
        case 'DesignService':
            return {
                title: product.title,
                description: __('Professional designers on-hand to deliver custom design, fast.'),
                earnings: product.price,
            };
        default: // ProductFixedPriceProject
            return {
                title: product.title,
                description: product.description,
                earnings: product.pricing.earningsInUsd,
            };
    }
}

function DefaultCard({ user }: { user: Owner }) {
    return (
        <DisabledCardContainer data-testid="job-card">
            <ContentWrapper>
                <Desktop>
                    <ItemIcon />
                </Desktop>
                <Mobile>
                    <ContentWrapper display="flex" alignItems="center">
                        <ItemIcon />
                        <Box flexDirection="column">
                            <JobCardTitle url="#" text={FallbackCopy} />
                            <AuthorDisplay user={user} />
                        </Box>
                    </ContentWrapper>
                </Mobile>
            </ContentWrapper>
            <ContentWrapper flexDirection="column" flex="0 1 1000px" paddingTop="0.25bu">
                <Desktop>
                    <JobCardTitle url="#" text={FallbackCopy} />
                    <AuthorDisplay user={user} />
                </Desktop>
                <TextWithClampedOverflow rawHtml={FallbackCopy} size="size2" />
            </ContentWrapper>
        </DisabledCardContainer>
    );
}

export function JobCard({ jobForDesigner, onCtaActionCompleted, dismissible }: JobCardProps) {
    const {
        job: { id, createdAt, brief, product, flags, category, source, user },
    } = jobForDesigner;

    if (!brief) {
        Bugsnag.notify(new Error(`job ${id} is missing a brief`));
        return <DefaultCard user={user} />;
    }

    if (!product) {
        Bugsnag.notify(new Error(`brief with title ${brief.title} is missing a package`));
        return <DefaultCard user={user} />;
    }

    const { title, description, industries } = brief;
    const [color, partner] = partnerFromCategory(category?.key);
    const url = linkTo.browseProjectsJob(id);
    const details = getProductDetails(product);

    return (
        <PartnerContext.Provider value={{ color: color, partner: partner }}>
            <JobCardContainer data-testid="job-card">
                <Mobile>
                    <RouterLinkOverlay to={url} aria-label={title || FallbackCopy} />
                </Mobile>
                <ContentWrapper>
                    <Desktop>
                        <Link href={url} underline="none">
                            <ItemIcon categoryKey={category?.key} />
                        </Link>
                    </Desktop>
                    <Mobile>
                        <ContentWrapper display="flex" alignItems="center">
                            <ItemIcon categoryKey={category?.key} />
                            <Box flexDirection="column">
                                <JobCardTitle url={url} text={title || FallbackCopy} />
                                <AuthorDisplay user={user} />
                            </Box>
                        </ContentWrapper>
                    </Mobile>
                </ContentWrapper>
                <ContentWrapper flexDirection="column" flex="0 1 1000px" paddingTop="0.25bu">
                    <Desktop>
                        <JobCardTitle url={url} text={title || FallbackCopy} />
                        <AuthorDisplay user={user} />
                    </Desktop>
                    <TextWithClampedOverflow rawHtml={description || FallbackCopy} size="size2" />
                    <Inline space="0.5bu">
                        {(source === 'VISTAPRINT' || source === 'DEPRECATED_BIFROST') && (
                            <StyledWordmarkWrapper>
                                <VistaWordmark />
                            </StyledWordmarkWrapper>
                        )}
                        {category && <Tag appearance="transparent">{category.title}</Tag>}
                        {industries.map(({ key, title }) => (
                            <Tag key={key} appearance="transparent">
                                {title}
                            </Tag>
                        ))}
                        {flags.test && <Tag appearance="callout">Test</Tag>}
                    </Inline>
                </ContentWrapper>
                <ActionsInfoWrapper>
                    <ActionsWrapper>
                        <Desktop>
                            <Box display="flex" width="100%">
                                <Box flexGrow={1} marginRight="0.75bu">
                                    <Button href={url} appearance="secondary" width="fill">
                                        {__('View')}
                                    </Button>
                                </Box>
                                <Box flexShrink={1}>
                                    {dismissible && (
                                        <DismissJobButton
                                            jobId={id}
                                            onCompleted={onCtaActionCompleted}
                                            iconOnly={true}
                                        />
                                    )}
                                </Box>
                            </Box>
                        </Desktop>
                        <Mobile>
                            {dismissible && (
                                <DismissJobButton
                                    jobId={id}
                                    onCompleted={onCtaActionCompleted}
                                    iconOnly={true}
                                />
                            )}
                        </Mobile>
                    </ActionsWrapper>
                    <InfoWrapper>
                        <InfoItem>
                            <Box display="flex" alignItems="center">
                                <IconServer1 />
                            </Box>
                            <Tooltip title={__('The amount to be paid to the selected designer.')}>
                                <DottedUnderline>
                                    <Currency currency="USD" value={details.earnings} />
                                </DottedUnderline>
                            </Tooltip>
                        </InfoItem>
                        <InfoItem>
                            <Box display="flex" alignItems="center">
                                <IconDate1 />
                            </Box>
                            <RelativeTimeDisplay timestamp={createdAt} />
                        </InfoItem>
                        <InfoItem>
                            <Box display="flex" alignItems="center">
                                <IconService1 />
                            </Box>
                            <Tooltip title={details.description}>
                                <DottedUnderline>{details.title}</DottedUnderline>
                            </Tooltip>
                        </InfoItem>
                    </InfoWrapper>
                </ActionsInfoWrapper>
            </JobCardContainer>
        </PartnerContext.Provider>
    );
}
